<template>
  <article class="product-card">
    <!--
      **画像と商品名も詳細ページへのリンクにする**（2026-08-19 事業側判断）。
      支給は `product-img` が `div`・`product-name` が素の `h3` で、押せるのは
      `btn-productgrid` だけ。旧 `commons/productCard.vue` は画像を `NuxtLink` で
      包んでいたため、そのままだとリニューアルで押せなくなる。
      飛び先は3箇所とも `detailPath` ひとつから出す（決定#5）。
    -->
    <NuxtLink :to="detailPath" class="product-img">
      <img :src="imageUrl" :alt="productContent.webTitle" />
    </NuxtLink>
    <div class="product-info" :class="{ 'is-func-labeled': isFunctionalFood }">
      <div class="product-cat">{{ categoryName }}</div>
      <h3 class="product-name">
        <NuxtLink :to="detailPath">{{ productContent.webTitle }}</NuxtLink>
      </h3>
      <p v-if="isFunctionalFood" class="product-func-label">
        {{ PRODUCT_CARD_FUNCTIONAL_FOOD_LABEL }}
      </p>
      <p class="product-desc">{{ productContent.topSummary }}</p>
    </div>
    <NuxtLink :to="detailPath" class="btn-productgrid">
      <span class="btn-productgrid-text">詳しくみる</span>
    </NuxtLink>
  </article>
</template>

<script setup lang="ts">
/**
 * 支給デザインの `product-card`。支給37枚すべて同一構造で、中身は
 * `product-img` / `product-cat` / `product-name` / `product-desc` / `btn-productgrid` の5要素のみ。
 *
 * 支給デザインには**価格もアイコンバッジも無い**（旧 `commons/productCard.vue` は両方出していた）。
 * 支給HTMLの `href="#"` は37枚すべてダミーなので、飛び先は商品コードから組む。
 *
 * **押せるのは画像・商品名・ボタンの3箇所**（2026-08-19 事業側判断）。支給は
 * `btn-productgrid` だけがリンクだが、旧実装は画像を `NuxtLink` で包んでおり、
 * 支給どおりにすると押せなくなる。**見た目は変えない** — `.product-img` は
 * `display: flex`（`style.css:1662`）をクラスから受けるので `div` → `NuxtLink` で崩れず、
 * 色と下線もリセット済み（`renewal/reset.css:35` の `text-decoration: none`、
 * `style.css:59` の `a { color: inherit }`）。ホバーの `opacity: 0.65`
 * （`同:893`）は他のリンクと同じ挙動。
 * 商品名・説明文・画像は API 由来なのでハードコードしない（決定#5・§3.7）。
 *
 * 絞り込み用の `data-cat` / `data-pickup` は付けない。支給JSは全件をDOMに置いて
 * `is-hidden` で隠す方式だが、Vue では `computed` で配列を絞るのでDOM自体を作らない。
 *
 * 機能性表示食品の注記（`product-func-label`）は支給に無い6要素目。対象商品は
 * `ProductCardConst` に持ち、装飾は支給CSSを避けて `vue-adjust.css` に置く。
 */
import type { ProductContent } from '@/types/domains/ProductContentType';
import { useProductContent } from '@/composables/useProductContent';
import {
  PRODUCT_CARD_CATEGORY_NAME,
  PRODUCT_CARD_FUNCTIONAL_FOOD_CODES,
  PRODUCT_CARD_FUNCTIONAL_FOOD_LABEL,
} from '@/consts/ProductCardConst';
import { assembleDetailPagePath } from '@/utils/StringHelper';
import { getProductImageUrl } from '@/utils/ImageHelper';

type Props = {
  productContent: ProductContent;
};
const props = defineProps<Props>();

const imageUrl = computed<string>(() =>
  getProductImageUrl(props.productContent.productCode, 'front_top_product')
);

const detailPath = computed<string>(() => assembleDetailPagePath(props.productContent.productCode));

const { resolveCategoryId } = useProductContent();

/**
 * `product-cat` はカテゴリIDだけで決まる（→ `ProductCardConst`）。
 * IDは商品コードの対応表を正として引く（→ `ProductContentConst` の `PRODUCT_CODE_TO_CATEGORY_ID`）。
 */
const categoryName = computed<string>(
  () => PRODUCT_CARD_CATEGORY_NAME[resolveCategoryId(props.productContent)] ?? ''
);

const isFunctionalFood = computed<boolean>(() =>
  PRODUCT_CARD_FUNCTIONAL_FOOD_CODES.includes(props.productContent.productCode)
);
</script>
